import React, { useMemo, useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import moment from 'moment';
import numeral from 'numeral';

import Button from 'ui-kit/button/button';
import DataGrid, { SORT_DIRECTION } from 'ui-kit/data-grid/data-grid';
import Dialog from 'ui-kit/dialog/dialog';

import './style.css';

export const PropertyImageCarousel = ({ images, isOpen, onClose, startingIndex }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(startingIndex);
    const handleCarouselButtonClick = ({ direction }) => {
        const nextImageIndex = currentImageIndex + direction;
        if (nextImageIndex >= images.length) setCurrentImageIndex(0);
        else if (nextImageIndex < 0) setCurrentImageIndex(images.length - 1);
        else setCurrentImageIndex(nextImageIndex);
    };

    const classes = classNames('property-images-carousel', { open: isOpen });

    const currentImage = useMemo(() => images[currentImageIndex], [currentImageIndex]);

    useEffect(() => {
        setCurrentImageIndex(startingIndex);
    }, [startingIndex]);

    return (
        <div className={classes}>
            <div className="property-images-carousel-blanket" onClick={onClose} role="button" tabIndex={0}>
                <button
                    type="button"
                    className="carousel-direction-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        handleCarouselButtonClick({ direction: -1 });
                    }}
                >
                    Prev
                </button>
                <button
                    type="button"
                    className="carousel-direction-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        handleCarouselButtonClick({ direction: 1 });
                    }}
                >
                    Next
                </button>
            </div>
            <div className="property-images-carousel-content">
                <FontAwesomeIcon icon="times" className="close-icon" onClick={onClose} />

                <div className="current-image-container">
                    <img src={currentImage} alt="" />
                </div>
            </div>
        </div>
    );
};

const ImageButton = ({ className, src, onClick }) => {
    return (
        <button type="button" className="image-button" onClick={onClick}>
            <img src={src} alt="" className={className} />
        </button>
    );
};
const PropertyImages = ({ images }) => {
    const [isCarouselOpen, setIsCarouselOpen] = useState(false);
    const [carouselImageIndex, setCarouselImageIndex] = useState(0);

    const mainImage = images[0];

    const secondaryImages = [...images];
    secondaryImages.shift();

    return (
        <div className="property-images">
            <div className="main-image">
                <ImageButton
                    src={mainImage}
                    onClick={() => {
                        setCarouselImageIndex(0);
                        setIsCarouselOpen(true);
                    }}
                />
            </div>
            <ul className="property-image-list">
                {secondaryImages &&
                    secondaryImages.map((imgSrc, idx) => (
                        <li key={`property-images-list-item-${idx}`}>
                            <ImageButton
                                src={imgSrc}
                                onClick={() => {
                                    setCarouselImageIndex(idx + 1);
                                    setIsCarouselOpen(true);
                                }}
                            />
                        </li>
                    ))}
            </ul>
            <PropertyImageCarousel
                isOpen={isCarouselOpen}
                onClose={() => setIsCarouselOpen(false)}
                images={images}
                startingIndex={carouselImageIndex}
            />
        </div>
    );
};

export const PropertyDetails = ({ bids, property }) => {
    const { description, isBiddingOpen, parcelNumber, startingBid } = property;
    const bidColumns = [
        {
            field: 'id',
            key: true,
            hide: true
        },
        {
            label: 'Date',
            field: 'dateSubmitted',
            cellRenderer: (date) => {
                return moment(date).format('MMM Do, YYYY - h:mm:ss A');
            }
        },
        {
            label: 'Amount',
            field: 'amount',
            cellRenderer: (amount) => {
                return numeral(amount).format('$0,0');
            },
            sort: (a, b, sortDirection) => {
                const numberA = Number(a.replace('$', ''));
                const numberB = Number(b.replace('$', ''));

                if (sortDirection === SORT_DIRECTION.ASC) {
                    if (numberA < numberB) return -1;
                    return numberA > numberB ? 1 : 0;
                }

                if (numberA < numberB) return 1;
                return numberA > numberB ? -1 : 0;
            }
        }
    ];
    return (
        <div className="property-details">
            <div>Parcel #: {parcelNumber}</div>
            <div>Starting Bid: ${startingBid}</div>
            <div>Bidding Status: {isBiddingOpen ? <strong>Open</strong> : <strong>Closed</strong>}</div>
            <h5 className="mt-3">About this property</h5>
            <div className="property-description">{description}</div>
            <h5 className="mt-4">Current Bids</h5>
            {bids && bids.length > 0 && (
                <DataGrid id="public-bids-list" columns={bidColumns} data={bids} defaultSortCol={bidColumns[2]} />
            )}
            {!bids ||
                (bids.length === 0 && (
                    <div className="no-current-bids">
                        There are currently no bids on this property. The minimum bid is <strong>${startingBid}</strong>
                        . Press the &apos;Place Bid&apos; button to place a bid on this property.
                    </div>
                ))}
        </div>
    );
};
const PropertyDialog = ({ bids, onPlaceBidClick, property, ...rest }) => {
    if (!property) {
        return <div className="no-dialog-content">Whoops, something went wrong..</div>;
    }

    const { address, isBiddingOpen } = property;
    const images = property?.images?.length > 0 ? property?.images?.split(',') : [];
    const hasImages = images && images.length > 0;

    return (
        <Dialog
            className="property-dialog"
            actions={<Button disabled={!isBiddingOpen} label="Place Bid" onClick={onPlaceBidClick} />}
            title={address}
            {...rest}
        >
            <Row className="w-100">
                {images && images.length > 0 && (
                    <Col xs={12} sm={12} md={6}>
                        <PropertyImages images={images} />
                    </Col>
                )}
                <Col xs={12} sm={12} md={hasImages ? 6 : 12}>
                    <PropertyDetails bids={bids} property={property} />
                </Col>
            </Row>
        </Dialog>
    );
};

export default PropertyDialog;
